import React, { useState } from "react";
import { Mail, Lock, Eye, EyeOff, Loader2, AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContextSupabase";
import { Link } from "react-router-dom";

const LoginSupabase = () => {
  const { signIn, resetPassword, loading, error } = useAuth();

  const [mode, setMode] = useState("login"); // login, forgot
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  // Logo CoryntiQ
  const Logo = ({ size = 80 }) => (
    <div
      className="relative flex-shrink-0 mx-auto"
      style={{ width: size, height: size }}
    >
      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-slate-400 to-slate-500"></div>
      <div
        className="absolute rounded-full bg-gradient-to-br from-sky-300 to-sky-400"
        style={{ top: "10%", left: "10%", width: "80%", height: "80%" }}
      ></div>
      <div
        className="absolute rounded-full bg-gradient-to-br from-slate-700 to-slate-800"
        style={{ top: "25%", left: "25%", width: "55%", height: "55%" }}
      ></div>
    </div>
  );

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setFormError("");
    setSuccessMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setSuccessMessage("");

    // Validation
    if (!formData.email || !formData.email.includes("@")) {
      setFormError("Veuillez entrer une adresse courriel valide.");
      return;
    }

    if (mode === "forgot") {
      const result = await resetPassword(formData.email);
      if (result.success) {
        setSuccessMessage("Un courriel de réinitialisation a été envoyé !");
      } else {
        setFormError(result.error || "Erreur lors de l'envoi du courriel.");
      }
      return;
    }

    if (formData.password.length < 6) {
      setFormError("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }

    const result = await signIn(formData.email, formData.password);

    if (!result.success) {
      setFormError(translateError(result.error));
    }
  };

  // Traduire les erreurs Supabase en français
  const translateError = (error) => {
    const translations = {
      "Invalid login credentials": "Courriel ou mot de passe incorrect.",
      "Email not confirmed":
        "Veuillez confirmer votre courriel avant de vous connecter.",
      "User already registered": "Un compte existe déjà avec ce courriel.",
      "Password should be at least 6 characters":
        "Le mot de passe doit contenir au moins 6 caractères.",
      "Unable to validate email address: invalid format":
        "Format de courriel invalide.",
    };
    return translations[error] || error || "Une erreur est survenue.";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Logo size={80} />
          <h1 className="text-3xl font-bold text-white mt-4 tracking-wide">
            CORYNTI<span className="text-sky-300">Q</span>
          </h1>
          <p className="text-slate-300 mt-2">
            Conformité Loi 25 - Protection des renseignements personnels
          </p>
        </div>

        {/* Formulaire */}
        <div className="bg-white rounded-xl shadow-2xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {mode === "login" && "Connexion"}
            {mode === "forgot" && "Mot de passe oublié"}
          </h2>

          {/* Messages */}
          {(formError || error) && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle size={18} />
              <span className="text-sm">{formError || error}</span>
            </div>
          )}

          {successMessage && (
            <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 text-sm">
              {successMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adresse courriel
              </label>
              <div className="relative">
                <Mail
                  size={18}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  placeholder="votre@courriel.com"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Mot de passe */}
            {mode !== "forgot" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mot de passe
                </label>
                <div className="relative">
                  <Lock
                    size={18}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                    placeholder="••••••••"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            )}

            {/* Bouton submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 rounded-lg font-medium text-white bg-slate-700 hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Chargement...
                </>
              ) : (
                <>
                  {mode === "login" && "Se connecter"}
                  {mode === "forgot" && "Envoyer le lien"}
                </>
              )}
            </button>
          </form>

          {/* Liens */}
          <div className="mt-6 text-center space-y-2">
            {mode === "login" && (
              <>
                <button
                  onClick={() => setMode("forgot")}
                  className="text-sm text-sky-600 hover:text-sky-700"
                >
                  Mot de passe oublié ?
                </button>
                <p className="text-sm text-gray-600">
                  Pas encore de compte ?{" "}
                  <Link
                    to="/signup"
                    className="text-sky-600 hover:text-sky-700 font-medium"
                  >
                    Créer un compte
                  </Link>
                </p>
              </>
            )}

            {mode === "forgot" && (
              <button
                onClick={() => setMode("login")}
                className="text-sm text-sky-600 hover:text-sky-700"
              >
                ← Retour à la connexion
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-slate-400">
          © 2026 CoryntiQ - CIGP inc. - Tous droits réservés
        </p>
      </div>
    </div>
  );
};

export default LoginSupabase;
